import { useState, useEffect, useCallback } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/concierge`;
const KEY = 'eliteiv_concierge_session';

const getSessionId = () => {
  let id = localStorage.getItem(KEY);
  if (!id) {
    id = `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(KEY, id);
  }
  return id;
};

export const useConcierge = () => {
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const sessionId = getSessionId();

  useEffect(() => {
    fetch(`${API}/history/${sessionId}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setMessages(data.map(({ role, content }) => ({ role, content }))))
      .catch(() => {});
  }, [sessionId]);

  const send = useCallback(async (text) => {
    setSending(true);
    setMessages((prev) => [...prev, { role: 'user', content: text }, { role: 'assistant', content: '', streaming: true }]);

    const patchLast = (fn) =>
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = fn(next[next.length - 1]);
        return next;
      });

    try {
      const res = await fetch(`${API}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const evt = JSON.parse(line.slice(6));
          if (evt.delta) patchLast((m) => ({ ...m, content: m.content + evt.delta }));
          if (evt.error) patchLast((m) => ({ ...m, content: evt.error }));
        }
      }
      patchLast((m) => ({ ...m, streaming: false }));
    } catch (e) {
      const msg = String(e).includes('429')
        ? 'You have sent quite a few messages. Please wait a moment or call 727-503-2572.'
        : 'The concierge is momentarily unavailable. Please try again or call 727-503-2572.';
      patchLast((m) => ({ ...m, content: msg, streaming: false }));
    } finally {
      setSending(false);
    }
  }, [sessionId]);

  return { messages, sending, send };
};
